import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { VideoToolTip } from "@components/video-tooltip";
import {
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { formatDateRange } from "@shared/date-range-picker";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";

import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

import { CompareDateRangeMenu } from "./compare-date-range-menu/compare-date-range-menu";
import { DateFilters } from "./date-filters";

export const FilterBreakdownBar = () => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRange,
    dateRangeInterval,
    compareDateRange,
    compareDateRangeInterval,
    breakdowns,
    filters,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
  } = useSalesPageV3NextContext();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  const compareDateRangeDisplayText = compareDateRangeInterval
    ? formatDateRange(compareDateRangeInterval)
    : "";

  return (
    <div
      className={clsx(
        "pt-4 flex gap-3",
        // { "pb-4": !doNotShowCharts }
      )}
    >
      <TimeGranularityV2Select
        label="GRANULARITY"
        timeGranularity={timeGranularity}
        onChange={(value: ITimeGranularityV2) => {
          prettyLogEventPage({
            domain: "Top Level Filters",
            action: "onChange",
            additionalActionInfo: "Set Time Granularity",
            data: {
              newTimeGranularity: value,
              pageContext,
            },
          });

          if (value === "hourly") {
            setDateRange(DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value]);
          } else {
            setDateRange({
              startDate: dateRangeInterval.startDate,
              endDate: dateRangeInterval.endDate,
              type: "fixed",
            });
          }

          setTimeGranularity(value);
        }}
        width="92px"
        icon={null}
      />
      <div className="text-xs">
        <div
          className={clsx(
            "text-xs text-interstellar+2 mb-1 leading-4 tracking-[0.025em]",
            "mt-[3px] cursor-default",
          )}
        >
          TIME PERIOD
          <span className="ml-2 text-gray+1">
            {formatDateRange(dateRangeInterval)}
          </span>
        </div>
        <DateFilters />
      </div>
      <div>
        <div
          className={clsx(
            "text-xs text-interstellar+2 mb-1 leading-4 tracking-[0.025em]",
            "mt-[3px] cursor-default flex items-center",
          )}
        >
          TIME COMPARISON
          <span className="ml-2 text-gray+1">
            {compareDateRangeDisplayText}
          </span>
          <VideoToolTip
            videoUrl="https://www.youtube.com/embed/D3kl9ji68dk?si=yNsRv2vyO9ZYU_Mu"
            contentDetails={{
              title: "Time comparisons",
              content:
                "A guided walk through how to compare time periods like YoY, WoW, " +
                "and custom time periods.",
              kbLink:
                "https://info.northbeam.io/knowledge/how-do-i-compare-time-periods-on-the-sales-page",
            }}
          />
        </div>
        <CompareDateRangeMenu
          className="h-8 w-64"
          timeGranularity={timeGranularity}
          compareDateRange={compareDateRange}
          onCompareDateRangeUpdate={(value) => {
            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Compare Date Range",
              data: {
                newCompareDateRange: value,
                pageContext,
              },
            });
            setCompareDateRange(value);
          }}
        />
      </div>
    </div>
  );
};
